import { z } from 'zod';

// NOTE: Zod v4 에러 타입들을 z.core 네임스페이스에서 import
type ZodIssueInvalidType = z.core.$ZodIssueInvalidType;
type ZodIssueTooBig = z.core.$ZodIssueTooBig;
type ZodIssueTooSmall = z.core.$ZodIssueTooSmall;
type ZodIssueNotMultipleOf = z.core.$ZodIssueNotMultipleOf;
type ZodIssueInvalidValue = z.core.$ZodIssueInvalidValue;

/**
 * Zod v4 에러를 사람이 읽기 쉬운 형태로 변환
 * @param zodError Zod 에러 객체
 * @returns 파싱된 에러 메시지 배열
 */
export const parseZodError = (zodError: z.ZodError): string[] =>
  zodError.issues.map((error) => {
    const { code, path, message } = error;

    // 경로를 사람이 읽기 쉬운 형태로 변환
    const pathString =
      path.length > 0
        ? path
            .map((p, index) => {
              if (typeof p === 'number') {
                return `${index > 0 ? '의 ' : ''}${p}번째 요소`;
              }
              return p;
            })
            .join('')
        : '';

    // 에러 타입별로 메시지 생성
    switch (code) {
      case 'invalid_type': {
        const invalidTypeError = error as ZodIssueInvalidType;
        // Zod v4에서는 message에서 받은값을 추출
        const receivedMatch = message.match(/received (\w+)/);
        const received = receivedMatch ? receivedMatch[1] : 'unknown';
        return `(${pathString}) 기대값: ${invalidTypeError.expected}, 받은값: ${received}`;
      }

      case 'invalid_value': {
        // Zod v4에서 invalid_literal, invalid_enum_value가 invalid_value로 통합됨
        const invalidValueError = error as ZodIssueInvalidValue;
        // Zod v4에서는 message에서 받은값을 추출하거나 기본 메시지 사용
        const receivedMatch = message.match(/expected "([^"]+)"/);
        const received = receivedMatch ? receivedMatch[1] : 'unknown';
        return `(${pathString}) 허용되지 않은 값입니다. 허용값: ${invalidValueError.values.join(
          ', ',
        )}, 받은값: ${received}`;
      }

      case 'invalid_union': {
        return `(${pathString}) 허용된 타입 중 하나와 일치해야 합니다.`;
      }

      case 'unrecognized_keys': {
        const unrecognizedError = error;
        return `(${pathString}) 허용되지 않은 키가 포함되어 있습니다: ${unrecognizedError.keys.join(
          ', ',
        )}`;
      }

      case 'invalid_format': {
        const { format } = error;

        const validationMessages: Record<string, string> = {
          email: '이메일 형식이 올바르지 않습니다.',
          url: 'URL 형식이 올바르지 않습니다.',
          uuid: 'UUID 형식이 올바르지 않습니다.',
          regex: '정규식 패턴과 일치하지 않습니다.',
          datetime: '날짜시간 형식이 올바르지 않습니다.',
          date: '날짜 형식이 올바르지 않습니다.',
          time: '시간 형식이 올바르지 않습니다.',
          ipv4: 'IPv4 주소 형식이 올바르지 않습니다.',
          ipv6: 'IPv6 주소 형식이 올바르지 않습니다.',
          base64: 'Base64 형식이 올바르지 않습니다.',
          base64url: 'Base64URL 형식이 올바르지 않습니다.',
          cuid: 'CUID 형식이 올바르지 않습니다.',
          cuid2: 'CUID2 형식이 올바르지 않습니다.',
          ulid: 'ULID 형식이 올바르지 않습니다.',
          nanoid: 'NanoID 형식이 올바르지 않습니다.',
          emoji: '이모지 형식이 올바르지 않습니다.',
          duration: '기간 형식이 올바르지 않습니다.',
          cidrv4: 'CIDR v4 형식이 올바르지 않습니다.',
          cidrv6: 'CIDR v6 형식이 올바르지 않습니다.',
        };

        const validationMessage =
          validationMessages[format] || `문자열 형식이 올바르지 않습니다. (${format})`;
        return `(${pathString}) ${validationMessage}`;
      }

      case 'too_small': {
        const tooSmallError = error as ZodIssueTooSmall;
        const typeMessages: Record<string, string> = {
          array: '개',
          string: '글자',
          number: '',
          set: '개',
          date: '',
          bigint: '',
        };

        const unit = typeMessages[tooSmallError.origin] || '';
        const minMessage =
          tooSmallError.minimum !== undefined
            ? `최소 ${tooSmallError.minimum}${unit} 이상이어야 합니다.`
            : '값이 너무 작습니다.';
        return `(${pathString}) ${minMessage}`;
      }

      case 'too_big': {
        const tooBigError = error as ZodIssueTooBig;
        const typeMessages: Record<string, string> = {
          array: '개',
          string: '글자',
          number: '',
          set: '개',
          date: '',
          bigint: '',
        };

        const unit = typeMessages[tooBigError.origin] || '';
        const maxMessage =
          tooBigError.maximum !== undefined
            ? `최대 ${tooBigError.maximum}${unit} 이하여야 합니다.`
            : '값이 너무 큽니다.';
        return `(${pathString}) ${maxMessage}`;
      }

      case 'not_multiple_of': {
        const notMultipleError = error as ZodIssueNotMultipleOf;
        return `(${pathString}) ${notMultipleError.divisor}의 배수여야 합니다.`;
      }

      case 'invalid_key': {
        // Zod v4에서 새로 추가된 에러 타입 (z.record/z.map용)
        return `(${pathString}) 유효하지 않은 키입니다.`;
      }

      case 'invalid_element': {
        // Zod v4에서 새로 추가된 에러 타입 (z.map/z.set용)
        return `(${pathString}) 유효하지 않은 요소입니다.`;
      }

      case 'custom': {
        return `(${pathString}) ${message}`;
      }

      default: {
        return `(${pathString}) ${message}`;
      }
    }
  });

/**
 * 서비스 공용 에러 처리 모듈 (Zod v4 호환)
 * @param error 에러 객체
 */
export const serviceErrorLogger = (error: unknown) => {
  if (error instanceof z.ZodError) {
    const parsedErrors = parseZodError(error);
    console.error('유효성 검증 실패:');
    parsedErrors.forEach((errorMessage, index) => {
      console.error(`${index + 1}. ${errorMessage}`);
    });
    return;
  }
  if (error instanceof Error) {
    console.error('서비스 실패:', error.message);
    return;
  }
  console.error('서비스 실패: 알 수 없는 오류');
};
